import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import { Box, Card, CardActionArea, CardContent, CardMedia, Typography } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import CameraIcon from '@material-ui/icons/AddAPhoto';
import AttachmentIcon from '@material-ui/icons/Attachment';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import fallbackImage from 'assets/images/stock-photos/fallback-image.png';
import { RMSButton } from '../../../shared/components/buttons';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import PersonPhotoDialog from 'features/user/components/add-edit-person/PersonPhotoDialog';
import PersonWebcamCapture from './PersonWebcamCapture';
import { getPersonImages, deletePersonImage, getPersonImage } from '../../services/userService';
import { NO_PHOTO_FILE_NAME } from '../../constants/personPhotoFormConstants';
import { base64ToBlob } from '../../utility/personPhotoHelper';
import FormSpinner from 'components/FormSpinner';
import { formatDateTime } from 'features/shared/utility/dateFormatter';

const PersonPhotoTab = forwardRef(({ personId }, ref) => {
  const dispatch = useDispatch();
  const canEditPerson = usePermissions(PERMISSION_RESOURCES.PERSON).Edit;
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [photoOnlyFilter, setPhotoOnlyFilter] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const webcamRef = useRef(null);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  useImperativeHandle(
    ref,
    () => ({ isDirty: () => camOn && Boolean(webcamRef.current?.isDirty?.()) }),
    [camOn]
  );

  const loadImages = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPersonImages(personId),
      onSuccess: (data) => setImages(data || []),
    });
  }, [dispatch, personId]);

  const filteredImages = useMemo(
    () =>
      photoOnlyFilter ? images.filter((image) => image.FileName !== NO_PHOTO_FILE_NAME) : images,
    [images, photoOnlyFilter]
  );

  const hasDefaultPhoto = useMemo(() => images.some((image) => image.IsDefault), [images]);

  const handleAdd = useCallback(() => {
    setEditingImage(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback(() => {
    if (!selected) return;
    setEditingImage(selected);
    setFormOpen(true);
  }, [selected]);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingImage(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadImages();
  }, [handleFormClose, loadImages]);

  const handleDelete = useCallback(() => {
    if (!selected) return;

    requestConfirm(
      'Are you sure you want to remove this photo?',
      async () => {
        await executeApiAction({
          setLoading: setDeleting,
          dispatch,
          action: () => deletePersonImage(selected.ptsImageID),
          onSuccess: () => {
            dispatch(notify('Photo deleted successfully', 'success'));
            setSelected(null);
            loadImages();
          },
        });
      },
      'Delete Photo'
    );
  }, [selected, dispatch, requestConfirm, loadImages]);

  const handleDownload = useCallback(async () => {
    if (!selected) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: async () => {
        const response = await getPersonImage(selected.ptsImageID);
        const blob = new Blob([response.buffer], { type: response.type });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = selected.FileName;
        link.click();

        window.URL.revokeObjectURL(url);
      },
    });
  }, [selected, dispatch]);

  const handleCopy = useCallback(async () => {
    if (!selected?.Image) return;

    try {
      const blob = base64ToBlob(selected.Image, 'image/png');

      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      dispatch(notify('Copied to clipboard', 'success'));
    } catch (err) {
      dispatch(notify('Unable to copy image to clipboard', 'error'));
    }
  }, [selected, dispatch]);

  const handleCameraOn = useCallback(() => setCamOn(true), []);
  const handleCameraOff = useCallback(() => setCamOn(false), []);
  const handlePhotoOnlyFilterChange = useCallback(
    (_, value) => value !== null && setPhotoOnlyFilter(value),
    []
  );

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  return (
    <>
      {loading && <FormSpinner />}
      <Box className="person-photo-tab">
        <Box className="person-photo-tab__actions">
          <ToggleButtonGroup
            value={photoOnlyFilter}
            exclusive
            size="small"
            onChange={handlePhotoOnlyFilterChange}>
            <ToggleButton value={false}>All</ToggleButton>
            <ToggleButton value={true}>With Photo Only</ToggleButton>
          </ToggleButtonGroup>

          <Box className="person-photo-tab__actions-right">
            <RMSButton
              label="Camera"
              variant="outlined"
              prefixIcon={<CameraIcon />}
              disabled={camOn || !canEditPerson}
              onClick={handleCameraOn}
            />
            <RMSButton
              label="Add Photo"
              variant="outlined"
              prefixIcon={<AttachmentIcon />}
              onClick={handleAdd}
              disabled={!canEditPerson}
            />
            {selected && (
              <>
                <RMSButton
                  label="Edit"
                  variant="outlined"
                  prefixIcon={<EditIcon />}
                  onClick={handleEdit}
                  disabled={!canEditPerson}
                />
                <RMSButton
                  label="Delete"
                  variant="outlined"
                  prefixIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  disabled={!canEditPerson}
                />
                <RMSButton
                  label="Download"
                  variant="outlined"
                  prefixIcon={<GetAppIcon />}
                  onClick={handleDownload}
                />
                <RMSButton
                  label="Copy"
                  variant="outlined"
                  prefixIcon={<FileCopyIcon />}
                  onClick={handleCopy}
                />
              </>
            )}
          </Box>
        </Box>

        {camOn && (
          <Box mb={2}>
            <PersonWebcamCapture
              ref={webcamRef}
              personId={personId}
              hasDefaultPhoto={hasDefaultPhoto}
              onSaved={loadImages}
              onClose={handleCameraOff}
            />
          </Box>
        )}

        <Box className="person-photo-tab__grid">
          {filteredImages.map((image) => (
            <Card
              key={image.ptsImageID}
              className={clsx(
                'person-photo-tab__card',
                image.ptsImageID === selected?.ptsImageID && 'person-photo-tab__card--selected'
              )}
              onClick={() => setSelected(image)}>
              <CardActionArea>
                <CardMedia
                  component="img"
                  alt={image.FileName}
                  height="220"
                  image={image.Image ? `data:image/jpeg;base64,${image.Image}` : fallbackImage}
                />
                <CardContent>
                  <Typography variant="body2" gutterBottom>
                    {image.ImageTypeDescription || image.ImageTypeCode}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Description: {image.Description || '-'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" component="div">
                    {formatDateTime(new Date(image.Created))}
                  </Typography>
                  {image.IsMark && <Typography variant="caption">Scar, Mark, or Tattoo</Typography>}
                  {image.IsDefault && <Typography variant="caption">Default Photo</Typography>}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}

          {!loading && filteredImages.length === 0 && (
            <Typography color="textSecondary">No images found</Typography>
          )}
        </Box>

        {formOpen && (
          <PersonPhotoDialog
            open={formOpen}
            personId={personId}
            photo={editingImage}
            hasDefaultPhoto={hasDefaultPhoto}
            onClose={handleFormClose}
            onSaved={handleSaved}
          />
        )}

        {confirmState.open && (
          <RMSConfirmationDialog
            open={confirmState.open}
            title={confirmState.title}
            message={confirmState.message}
            onNo={handleNo}
            onYes={handleYes}
            loading={deleting}
          />
        )}
      </Box>
    </>
  );
});

export default PersonPhotoTab;
